"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LuEye, LuEyeClosed } from "react-icons/lu";
import image from "../../../assets/login.jpg";
import { childVariants, containerVariants } from "./motion";
import { useNavigate } from "react-router-dom";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const input = [
  { name: "firstName", label: "First Name", type: "text", minLength: 2, maxLength: 15 },
  { name: "lastName", label: "Last Name", type: "text", minLength: 2, maxLength: 15 },
  { name: "email", label: "Email Address", type: "email", maxLength: 254 },
  { name: "phone", label: "Phone Number (optional)", type: "tel", required: false, minLength: 7, maxLength: 15 },
  { name: "country", label: "Country", type: "text", minLength: 2, maxLength: 56 },
  { name: "username", label: "Username", type: "text", minLength: 3, maxLength: 30 },
  { name: "password", label: "Set Password", type: "password", minLength: 8, maxLength: 25 },
  { name: "confirmPassword", label: "Confirm Password", type: "password", minLength: 8, maxLength: 25 },
];

const alphaOnlyFields = [];
const alphaWithSpaceFields = ["firstName", "lastName", "country"];

const SignupForm = ({ setStep, setTempToken }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "",
    username: "",
    password: "",
    role: "client",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
    general: "",
  });

  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    maxLength: true,
    hasLetter: false,
    hasNumber: false,
    match: false,
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [allCriteriaMet, setAllCriteriaMet] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });

  useEffect(() => {
    const requiredFields = input
      .filter((field) => field.required !== false)
      .map((field) => field.name);

    const allFieldsFilled = requiredFields.every(
      (field) => formData[field].trim() !== ""
    );

    const passwordValid =
      passwordStrength.length &&
      passwordStrength.maxLength &&
      passwordStrength.hasLetter &&
      passwordStrength.hasNumber &&
      passwordStrength.match;

    const hasErrors = input.some((field) =>
      Boolean(validateField(field.name, formData[field.name], formData))
    );

    setIsFormValid(allFieldsFilled && passwordValid && !hasErrors);
  }, [formData, passwordStrength]);

  useEffect(() => {
    const password = formData.password;
    const confirmPassword = formData.confirmPassword;

    const newPasswordStrength = {
      length: password.length >= 8,
      maxLength: password.length <= 25,
      hasLetter: /[a-zA-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      match: password === confirmPassword && password !== "",
    };

    setPasswordStrength(newPasswordStrength);

    const allMet =
      newPasswordStrength.length &&
      newPasswordStrength.maxLength &&
      newPasswordStrength.hasLetter &&
      newPasswordStrength.hasNumber &&
      newPasswordStrength.match;

    if (allMet && password !== "") {
      setAllCriteriaMet(true);
      setShowSuccess(true);

      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 2000);

      return () => clearTimeout(timer);
    } else {
      setAllCriteriaMet(false);
      setShowSuccess(false);
    }
  }, [formData.password, formData.confirmPassword]);

  const getFieldConfig = (fieldName) => input.find((field) => field.name === fieldName);

  const sanitizeInput = (fieldName, value) => {
    let sanitizedValue = value;

    if (alphaOnlyFields.includes(fieldName)) {
      sanitizedValue = sanitizedValue.replace(/[^a-zA-Z]/g, "");
    }

    if (alphaWithSpaceFields.includes(fieldName)) {
      sanitizedValue = sanitizedValue.replace(/[^a-zA-Z\s]/g, "");
    }

    if (fieldName === "phone") {
      sanitizedValue = sanitizedValue.replace(/\D/g, "");
    }

    const fieldConfig = getFieldConfig(fieldName);
    if (fieldConfig?.maxLength) {
      sanitizedValue = sanitizedValue.slice(0, fieldConfig.maxLength);
    }

    return sanitizedValue;
  };

  const validateField = (fieldName, value, fullFormData = formData, rawValue = value) => {
    const fieldConfig = getFieldConfig(fieldName);
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const rawInput = typeof rawValue === "string" ? rawValue : "";

    if (alphaWithSpaceFields.includes(fieldName) && rawInput && /[^a-zA-Z\s]/.test(rawInput)) {
      return `${fieldConfig.label} should contain only letters and spaces`;
    }

    if (alphaOnlyFields.includes(fieldName) && rawInput && /[^a-zA-Z]/.test(rawInput)) {
      return `${fieldConfig.label} should contain only letters`;
    }

    if (fieldConfig?.required !== false && !trimmedValue) {
      return `${fieldConfig.label} is required`;
    }

    if (!trimmedValue && fieldConfig?.required === false) {
      return "";
    }

    if (alphaOnlyFields.includes(fieldName) && !/^[A-Za-z]+$/.test(trimmedValue)) {
      return `${fieldConfig.label} must contain only letters`;
    }

    if (alphaWithSpaceFields.includes(fieldName) && !/^[A-Za-z\s]+$/.test(trimmedValue)) {
      return `${fieldConfig.label} must contain only letters`;
    }

    if (fieldName === "phone" && !/^\d+$/.test(trimmedValue)) {
      return "Phone Number must contain only digits";
    }

    if (fieldConfig?.minLength && trimmedValue.length < fieldConfig.minLength) {
      return `${fieldConfig.label} must be at least ${fieldConfig.minLength} characters`;
    }

    if (fieldConfig?.maxLength && trimmedValue.length > fieldConfig.maxLength) {
      return `${fieldConfig.label} must be at most ${fieldConfig.maxLength} characters`;
    }

    if (fieldName === "password" && !(/[a-zA-Z]/.test(value) && /[0-9]/.test(value))) {
      return "Password must contain both letters and numbers";
    }

    if (fieldName === "confirmPassword" && value !== fullFormData.password) {
      return "Passwords do not match";
    }

    return "";
  };

  const validateForm = () => {
    const fieldErrors = {};
    const clearedFieldErrors = {};

    input.forEach((field) => {
      clearedFieldErrors[field.name] = "";
    });

    input.forEach((field) => {
      const errorMessage = validateField(field.name, formData[field.name], formData);
      if (errorMessage) {
        fieldErrors[field.name] = errorMessage;
      }
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      ...clearedFieldErrors,
      ...fieldErrors,
      general: "",
    }));

    return Object.keys(fieldErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    setErrors((prevErrors) => ({ ...prevErrors, general: "" }));

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          country: formData.country,
          username: formData.username,
          password: formData.password,
          role: formData?.role,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Something went wrong!");
      }


      localStorage.setItem("token", data.body.token)
      localStorage.setItem("user", JSON.stringify(data.body.newUser))
      if (data?.body?.role === "admin") {
        navigate("/admin")
      }
      if (data?.body?.role === "client") {
        navigate("/client")
      }
      if (data?.body?.role === "agent") {
        navigate("/agent")
      }
      if (data?.body?.role === "sub-admin") {
        navigate("/agent")
      }

    } catch (err) {
      setErrors({ ...errors, general: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, fieldName) => {
    const value = sanitizeInput(fieldName, e.target.value);
    const updatedFormData = {
      ...formData,
      [fieldName]: value,
    };

    setFormData(updatedFormData);

    const fieldError = validateField(fieldName, value, updatedFormData, e.target.value);
    setErrors((prevErrors) => {
      const nextErrors = {
        ...prevErrors,
        [fieldName]: fieldError,
      };

      if (fieldName === "password") {
        nextErrors.confirmPassword = validateField(
          "confirmPassword",
          updatedFormData.confirmPassword,
          updatedFormData
        );
      }

      return nextErrors;
    });
  };

  const togglePasswordVisibility = (fieldName) => {
    setShowPassword((prevState) => ({
      ...prevState,
      [fieldName]: !prevState[fieldName],
    }));
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="w-max bg-white/85 flex flex-row justify-center items-center border border-[#CBD5E1] rounded-[10px] shadow-custom overflow-hidden mt-12 md:mt-0"
    >
      <motion.div
        variants={childVariants}
        className="hidden lg:block w-[445px] 2xl:w-[630px] min-h-[655px] max-h-[708px] overflow-hidden"
      >
        <img
          src={image || "/placeholder.svg"}
          alt="signup"
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </motion.div>

      <motion.div
        variants={childVariants}
        className="flex flex-col w-[300px] md:w-[400px] 2xl:w-[500px] px-8 py-5"
      >
        <motion.form variants={childVariants} onSubmit={handleRegister}>
          {input.map((field) => (
            <motion.div
              variants={childVariants}
              className="mb-1.5"
              key={field.name}
            >
              <div className="flex justify-start items-center gap-1">
                <label className="block text-gray-700 text-sm">
                  {field.label}
                </label>
                {field.required !== false && (
                  <span className="text-[#DF6E6E] text-lg">&#42;</span>
                )}
              </div>
              <div className="relative">
                <input
                  type={field.type === "password" && showPassword[field.name] ? "text" : field.type}
                  value={formData[field.name]}
                  onChange={(e) => handleInputChange(e, field.name)}
                  className={`w-full px-2.5 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-[#BBBBBB] bg-[#F0F0F0] text-sm appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${field.type === "password" ? "pr-10 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden" : ""} ${errors[field.name] ? "border-red-500" : ""
                    }`}
                  required={field.required !== false}
                  maxLength={field.maxLength}
                  minLength={field.minLength}
                  inputMode={field.name === "phone" ? "numeric" : undefined}
                />

                {field.type === "password" && (
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility(field.name)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                    aria-label={showPassword[field.name] ? "Hide password" : "Show password"}
                  >
                    {showPassword[field.name] ? <LuEye className="w-4 h-4" /> : <LuEyeClosed className="w-4 h-4" />}
                  </button>
                )}
              </div>
              {errors[field.name] &&
                !(
                  (field.name === "password" && formData.password) ||
                  (field.name === "confirmPassword" &&
                    formData.confirmPassword &&
                    !passwordStrength.match &&
                    !allCriteriaMet)
                ) && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-xs text-red-500 mt-1"
                >
                  {errors[field.name]}
                </motion.p>
                )}

              {field.name === "password" &&
                formData.password &&
                !allCriteriaMet && (
                  <div className="mt-1 space-y-1">
                    <div className="text-xs text-red-500 space-y-0.5">
                      <AnimatePresence>
                        {!passwordStrength.length && (
                          <motion.p
                            key="length"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            • Password must be at least 8 characters
                          </motion.p>
                        )}

                        {!passwordStrength.hasLetter && (
                          <motion.p
                            key="letters"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            • Password must contain letters
                          </motion.p>
                        )}

                        {!passwordStrength.maxLength && (
                          <motion.p
                            key="max-length"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            • Password must be at most 25 characters
                          </motion.p>
                        )}

                        {!passwordStrength.hasNumber && (
                          <motion.p
                            key="numbers"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            • Password must contain numbers
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

              {field.name === "confirmPassword" &&
                formData.confirmPassword &&
                !passwordStrength.match &&
                !allCriteriaMet && (
                  <AnimatePresence>
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-xs text-red-500 mt-1"
                    >
                      • Passwords do not match
                    </motion.p>
                  </AnimatePresence>
                )}
            </motion.div>
          ))}

          <AnimatePresence>
            {showSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="mb-2"
              >
                <p className="text-sm text-green-500 font-medium">
                  ✓ Password requirements met successfully!
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex justify-center items-center mt-4">
            {loading ? (
              <button
                className="w-full px-4 py-2 text-white bg-[#025177] rounded-full h-[40px] flex items-center justify-center"
                disabled
              >
                <span className="loader"></span>
              </button>
            ) : (
              <button
                type="submit"
                disabled={!isFormValid}
                className={`w-full py-2 text-white rounded-full ${isFormValid
                    ? "bg-[#036494] hover:bg-[#025177] cursor-pointer"
                    : "bg-[#036494]/50 cursor-not-allowed"
                  }`}
              >
                Register
              </button>
            )}
          </div>

          <AnimatePresence>
            {errors.general && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-sm text-red-500 mt-1.5"
              >
                {errors.general}
              </motion.p>
            )}
          </AnimatePresence>
          <motion.div variants={childVariants} className="text-center text-sm text-gray-600 pt-1">
            Already have an account?{" "}
            <button onClick={() => navigate("/login")} type="button" className="text-[#036494] hover:underline">
              Log In?
            </button>
          </motion.div>
        </motion.form>
      </motion.div>
    </motion.div>
  );
};

export default SignupForm;